// Tailor GP surface — the SMOOTH residual-GP surface, sliced client-side
// (FXC-9009).
//
// "I do not want to see that stupid surface. I want to see Tailor." The "stupid
// surface" is the raw-binned SurfaceCorrelogram grid (32×32 bins of the raw
// scatter — spiky, dips negative). This renders Tailor's baked residual-GP
// field instead: fetched ONCE (`/composite/gp-field`), sliced LOCALLY by
// N-linear interpolation (gpField.sliceGpField), so dragging a held-value
// slider reslices the surface in real time with NO backend round-trip.
//
// The actual run points are PROJECTED onto the plot — each run at its
// (axisX, axisY) with its MEASURED quantity value as the sphere height — so you
// see data-vs-fit (the surface passing through, or departing from, the runs).
//
// three.js scaffolding (persistent scene controller, render-on-demand, minimal
// orbit) is adapted from TrustSurfacePanel. When no baked field exists
// (available:false) the caller falls back to the /surface path.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchGpField } from "../../api/composite";
import {
  applyEvidenceMode,
  axisMidpoint,
  buildSurfaceMeshData,
  convexHull2d,
  defaultHeld,
  fieldAxisDomains,
  rampColorCss,
  rampColorRgb,
  sliceGpField,
  surfaceHeight,
  SURFACE_LAYOUT,
  type GpEvidenceMode,
  type GpFieldAvailable,
  type SliceResult,
} from "./gpField";
import { Icons } from "../thread-v4/icons";

type ThreeModule = typeof import("three");

const LAYOUT = SURFACE_LAYOUT; // shared world scale (one framing camera + one mesh path)
const DEFAULT_VIEW: GpView = { yaw: -0.6, pitch: 0.32, zoom: 1 };
const BASE_CAMERA = { x: 2.6, y: 2.2, z: 2.6 } as const;
const SLICE_RES = 40;

export type GpView = { yaw: number; pitch: number; zoom: number };

interface Props {
  projectId: string;
  branch: string;
  quantity: string;
  axisX: string;
  axisY: string;
  /** Grow the 3D canvas to fill its container height (right-rail layout). */
  fill?: boolean;
  /** The explorer's rows (same allRows the 2D scatter uses). Each carries the
   *  condition axes + the quantity as flat numeric fields. Projected as points. */
  rows?: Record<string, unknown>[];
  /** Human labels per axis id, for the slider captions. */
  axisLabels?: Record<string, string>;
  /** Called when unavailable so the parent can fall back to /surface.
   *  reason: "no-field" = a real 200 said no baked field (sticky fallback);
   *  "error" = the fetch failed (transient — the parent should re-probe). */
  onUnavailable?: (unavailable: boolean, reason?: "no-field" | "error") => void;
  /** Reports the plane this panel ACTUALLY plots (its own X/Y pickers may
   * override the caller's axes) — so outer panels keyed on the plane (the rail
   * trust map) follow what the 3D view really shows (FXC-9009). */
  onPlaneChange?: (plane: { x: string; y: string }) => void;
  /** A run sphere was clicked — receives the row behind it (the composite opens
   * its provenance drawer / selects the run). */
  onRunSelect?: (row: Record<string, unknown>) => void;
}

function numeric(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

export interface ProjectedRun {
  x: number;
  y: number;
  value: number;
  /** Monte-Carlo ensemble members for a transported value (corrected mode,
   *  campaign path only). When present, the cell draws the members as a
   *  translucent cloud INSTEAD of the opaque sphere — the cloud is the point. */
  mc?: number[];
  /** The source row: full condition + identity (_row_id/_node_id). Feeds the
   *  hover tooltip, the colour-by axis lookup, and the evidence transport. */
  row?: Record<string, unknown>;
}

/** The runs projected onto the (axisX, axisY, quantity) plane — finite only. */
export function projectRuns(
  rows: Record<string, unknown>[] | undefined,
  axisX: string,
  axisY: string,
  quantity: string,
): ProjectedRun[] {
  if (!rows) return [];
  const out: ProjectedRun[] = [];
  for (const row of rows) {
    const x = numeric(row[axisX]);
    const y = numeric(row[axisY]);
    const value = numeric(row[quantity]);
    if (x === null || y === null || value === null) continue;
    out.push({ x, y, value, row });
  }
  return out;
}

// ── transport distance (FXC-9009): how far Corrected moved each run ─────────
/** One raw→corrected "stalk": a run that the transport actually moved. */
export interface TransportStalk {
  x: number;
  y: number;
  rawValue: number;
  correctedValue: number;
  delta: number;
}

/** Per-run transport deltas (corrected − raw), aligned by index with the input
 *  arrays (applyEvidenceMode's corrected mode maps 1:1 in order). Pure. */
export function transportDeltas(raw: ProjectedRun[], corrected: ProjectedRun[]): number[] {
  const n = Math.min(raw.length, corrected.length);
  const out = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i += 1) out[i] = corrected[i]!.value - raw[i]!.value;
  return out;
}

/** Stalk segments (raw position → corrected position) for the runs that the
 *  transport actually moved. Endpoints are exactly the raw/corrected values. */
export function buildTransportStalks(raw: ProjectedRun[], corrected: ProjectedRun[]): TransportStalk[] {
  const deltas = transportDeltas(raw, corrected);
  const out: TransportStalk[] = [];
  for (let i = 0; i < deltas.length; i += 1) {
    const delta = deltas[i]!;
    if (Math.abs(delta) < 1e-12) continue;
    const run = raw[i]!;
    out.push({ x: run.x, y: run.y, rawValue: run.value, correctedValue: corrected[i]!.value, delta });
  }
  return out;
}

/** Mean/max |Δ| over the moved runs — the caption readout + the colour/stalk
 *  normalisation basis. null when nothing moved. */
export function transportStats(stalks: TransportStalk[]): { mean: number; max: number } | null {
  if (!stalks.length) return null;
  let sum = 0;
  let max = 0;
  for (const stalk of stalks) {
    const a = Math.abs(stalk.delta);
    sum += a;
    if (a > max) max = a;
  }
  return { mean: sum / stalks.length, max };
}

/** Colour-by picker sentinel for "transport |Δ|" (only offered in corrected mode). */
export const TRANSPORT_COLOR_KEY = "__transport";

// ── GP training-point highlight (FXC-9009 "which 500") ───────────────────────
/** Colour-by picker sentinel for "GP training point" (needs trained keys). */
export const TRAINING_COLOR_KEY = "__gp_training";
/** Amber: this run ACTUALLY trained the residual GP (post-subsample). */
export const TRAINING_POINT_RGB: [number, number, number] = [0.94, 0.62, 0.16];
/** Dim slate: an untrained spectator — data in hand the GP never saw. */
export const SPECTATOR_POINT_RGB: [number, number, number] = [0.42, 0.45, 0.5];

/** The run's condition key (its row identity, `_row_id` = condition_key). */
export function runConditionKey(run: ProjectedRun): string | null {
  const id = run.row?._row_id;
  return typeof id === "string" && id ? id : null;
}

/** Binary highlight colours: training points amber, spectators dim. Pure. */
export function trainingPointColors(
  runs: ProjectedRun[],
  trained: ReadonlySet<string>,
): Array<[number, number, number]> {
  return runs.map((run) => {
    const key = runConditionKey(run);
    return key && trained.has(key) ? TRAINING_POINT_RGB : SPECTATOR_POINT_RGB;
  });
}

// ── solve order / budget replay (FXC-9009): chronological rank over the rows ──
/** Colour-by picker sentinel for "solve order" (needs timestamped rows). */
export const ORDER_COLOR_KEY = "__order";

const CHRONO_KEYS = ["created_at", "createdAt", "_created_at", "solved_at", "timestamp"];

/** Best-effort creation time of a row (epoch ms), from common timestamp keys.
 *  Accepts epoch numbers, parseable date strings, or numeric strings. */
export function chronoStamp(row: Record<string, unknown>): number | null {
  for (const key of CHRONO_KEYS) {
    const v = row[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim()) {
      const parsed = Date.parse(v);
      if (Number.isFinite(parsed)) return parsed;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/** Chronological rank (0 = earliest) per row object, over the rows that carry a
 *  timestamp. null when fewer than 2 rows are stamped — no order to replay. */
export function chronoRankMap(rows: Record<string, unknown>[]): Map<Record<string, unknown>, number> | null {
  const stamped: Array<{ row: Record<string, unknown>; t: number }> = [];
  for (const row of rows) {
    const t = chronoStamp(row);
    if (t !== null) stamped.push({ row, t });
  }
  if (stamped.length < 2) return null;
  stamped.sort((a, b) => a.t - b.t);
  const map = new Map<Record<string, unknown>, number>();
  stamped.forEach((entry, index) => map.set(entry.row, index));
  return map;
}

/** First-N-chronologically filter: keeps unstamped rows (they can't be ordered,
 *  hiding them would silently lie about the data), drops stamped rows ≥ n. */
export function filterRowsByBudget(
  rows: Record<string, unknown>[],
  rankMap: Map<Record<string, unknown>, number> | null,
  budget: number,
): Record<string, unknown>[] {
  if (!rankMap || budget >= rankMap.size) return rows;
  return rows.filter((row) => {
    const rank = rankMap.get(row);
    return rank === undefined || rank < budget;
  });
}

// ── data eras (FXC-9009): separate legacy sweeps from Tailor-picked data ──────
/** One heuristic "era": a gap-delimited session/campaign of runs. */
export interface DataEra {
  id: string;
  /** Start-day label, e.g. "Jun 24" (disambiguated with a counter if repeated). */
  label: string;
  start: number;
  end: number;
  count: number;
}

/** Heuristic session gap: timestamps further apart than this start a new era. */
export const ERA_GAP_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Cluster the rows' created_at timestamps into "eras" by gap-based session
 * splitting (HEURISTIC — a gap > gapMs starts a new era). Unstamped rows are
 * left unassigned (the era filter never hides them). Returns null when there
 * is nothing to separate: fewer than 2 stamped rows, or a single era.
 */
export function clusterEras(
  rows: Record<string, unknown>[],
  gapMs: number = ERA_GAP_MS,
): { eras: DataEra[]; eraByRow: Map<Record<string, unknown>, string> } | null {
  const stamped: Array<{ row: Record<string, unknown>; t: number }> = [];
  for (const row of rows) {
    const t = chronoStamp(row);
    if (t !== null) stamped.push({ row, t });
  }
  if (stamped.length < 2) return null;
  stamped.sort((a, b) => a.t - b.t);

  const eraByRow = new Map<Record<string, unknown>, string>();
  const eras: DataEra[] = [];
  const labelCounts = new Map<string, number>();
  const pushEra = (members: Array<{ row: Record<string, unknown>; t: number }>) => {
    if (!members.length) return;
    const id = `era-${eras.length}`;
    const start = members[0]!.t;
    const end = members[members.length - 1]!.t;
    const day = new Date(start).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const seen = (labelCounts.get(day) ?? 0) + 1;
    labelCounts.set(day, seen);
    const eraLabel = seen > 1 ? `${day} (${seen})` : day;
    for (const member of members) eraByRow.set(member.row, id);
    eras.push({ id, label: eraLabel, start, end, count: members.length });
  };

  let current: Array<{ row: Record<string, unknown>; t: number }> = [];
  for (const item of stamped) {
    if (current.length && item.t - current[current.length - 1]!.t > gapMs) {
      pushEra(current);
      current = [];
    }
    current.push(item);
  }
  pushEra(current);

  return eras.length >= 2 ? { eras, eraByRow } : null;
}

/** Rows with the hidden eras' members removed (unstamped rows always kept). */
export function filterRowsByEras(
  rows: Record<string, unknown>[],
  eraByRow: Map<Record<string, unknown>, string> | null,
  hiddenEras: ReadonlySet<string>,
): Record<string, unknown>[] {
  if (!eraByRow || !hiddenEras.size) return rows;
  return rows.filter((row) => {
    const era = eraByRow.get(row);
    return era === undefined || !hiddenEras.has(era);
  });
}

// ── era CHIP grouping (FXC-9009 IA): 15 session chips drowned the High-D view.
/** One era chip group: same-day sessions merged; toggling toggles every member. */
export interface EraGroup {
  id: string;
  label: string;
  count: number;
  /** Member era ids (≥1). Toggling the chip toggles all of them together. */
  eraIds: string[];
  start: number;
  end: number;
}

/** Max era chips shown before the rest fold behind a "+N more" expander. */
export const MAX_PRIMARY_ERA_CHIPS = 6;

/**
 * Compact the era list for display: (1) merge same-DAY sessions into one group
 * (their chips said the same thing twice); (2) keep at most `maxChips` primary
 * chips — the MOST RECENT group is ALWAYS primary (the live campaign is the
 * demo star), the rest ranked by run count; overflow goes behind an expander.
 * Chips stay in chronological order. Pure.
 */
export function groupEras(
  eras: DataEra[],
  maxChips: number = MAX_PRIMARY_ERA_CHIPS,
): { primary: EraGroup[]; overflow: EraGroup[] } {
  // 1) merge same-day sessions (label prefix before any " (n)" disambiguator)
  const byDay = new Map<string, EraGroup>();
  for (const era of eras) {
    const day = era.label.replace(/ \(\d+\)$/, "");
    const existing = byDay.get(day);
    if (existing) {
      existing.count += era.count;
      existing.eraIds.push(era.id);
      existing.start = Math.min(existing.start, era.start);
      existing.end = Math.max(existing.end, era.end);
    } else {
      byDay.set(day, { id: era.id, label: day, count: era.count, eraIds: [era.id], start: era.start, end: era.end });
    }
  }
  const groups = [...byDay.values()].sort((a, b) => a.start - b.start);
  if (groups.length <= maxChips) return { primary: groups, overflow: [] };
  // 2) the most recent group is always primary; fill the rest by run count
  const latest = groups[groups.length - 1]!;
  const rest = groups.slice(0, -1);
  const keep = new Set<string>([latest.id]);
  [...rest]
    .sort((a, b) => b.count - a.count)
    .slice(0, Math.max(0, maxChips - 1))
    .forEach((group) => keep.add(group.id));
  const primary = groups.filter((group) => keep.has(group.id));
  const overflow = groups.filter((group) => !keep.has(group.id));
  return { primary, overflow };
}

// ── imperative three.js controller (persists across data changes) ───────────
/** Per-content metadata: axis captions for the 3D axes + optional per-run
 *  colours (colour-by, [r,g,b] 0..1 triples aligned with `runs`) + optional
 *  raw→corrected transport stalks (corrected mode) + optional data-support
 *  hull outline + optional prior ghost slice (on the mean's height scale). */
type ContentMeta = {
  xLabel: string;
  yLabel: string;
  valueLabel: string;
  pointColors?: Array<[number, number, number]> | null;
  stalks?: TransportStalk[] | null;
  /** Convex hull of the in-slice runs on the (x,y) plane — beyond = pure model. */
  hull?: Array<{ x: number; y: number }> | null;
  /** The ROM prior sliced over the same plane/held — drawn as a ghost wireframe. */
  priorSlice?: SliceResult | null;
};

type SceneCtrl = {
  setContent: (slice: SliceResult, runs: ProjectedRun[], meta: ContentMeta) => void;
  applyView: (view: GpView) => void;
  /** Force an immediate paint plus a deferred second paint next frame — so a
   *  ResizeObserver landing the bounded canvas a frame late is captured. */
  paint: () => void;
  dispose: () => void;
};

/** Hover payload for the run-sphere tooltip: which run + canvas-local coords. */
export type RunHover = { index: number; left: number; top: number };

// tick / axis-label / caption number formatting for the 3D axes + readouts
export function formatTick(value: number): string {
  if (!Number.isFinite(value)) return "";
  const abs = Math.abs(value);
  if (abs !== 0 && (abs < 1e-3 || abs >= 1e5)) return value.toExponential(1);
  return String(Number(value.toPrecision(3)));
}

/** Canvas-texture text sprite (billboard) — light alternative to CSS2D overlay.
 *  Returns null when a 2d context is unavailable (jsdom) — axes then render as
 *  bare lines, which is fine headless. Exported so the High-D correlogram can
 *  draw the same per-cell axis labels. */
export function makeTextSprite(
  THREE: ThreeModule,
  text: string,
  opts: { height?: number; color?: string; bold?: boolean } = {},
): { sprite: InstanceType<ThreeModule["Sprite"]>; disposables: Array<{ dispose(): void }> } | null {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const fontPx = 44;
  ctx.font = `${opts.bold ? "700 " : ""}${fontPx}px Inter, system-ui, sans-serif`;
  const metrics = ctx.measureText(text);
  canvas.width = Math.max(2, Math.ceil(metrics.width) + 12);
  canvas.height = fontPx + 14;
  const ctx2 = canvas.getContext("2d");
  if (!ctx2) return null;
  ctx2.font = `${opts.bold ? "700 " : ""}${fontPx}px Inter, system-ui, sans-serif`;
  ctx2.textBaseline = "middle";
  ctx2.fillStyle = opts.color ?? "#8b93a1";
  ctx2.fillText(text, 6, canvas.height / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(material);
  const h = opts.height ?? 0.11;
  sprite.scale.set((h * canvas.width) / canvas.height, h, 1);
  return { sprite, disposables: [texture, material] };
}

async function createSceneCtrl(
  el: HTMLDivElement,
  opts: {
    onViewCommit?: (view: GpView) => void;
    /** Pointer is over a run sphere (canvas-local coords) — or left all spheres (null). */
    onHover?: (hover: RunHover | null) => void;
    /** Clean click (no drag) on a run sphere. */
    onPick?: (index: number) => void;
  },
): Promise<SceneCtrl> {
  const THREE = await import("three");
  const width = el.clientWidth || 480;
  const height = el.clientHeight || 320;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
  camera.position.set(BASE_CAMERA.x, BASE_CAMERA.y, BASE_CAMERA.z);
  camera.lookAt(0, 0, 0);
  // preserveDrawingBuffer: render-on-demand only paints on invalidate(); without
  // it the backbuffer can read/composite blank between frames (blank screenshots,
  // occasional flicker on resize). Small cost, reliably-persisted last frame.
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  el.appendChild(renderer.domElement);

  scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.05));
  const dir = new THREE.DirectionalLight(0xffffff, 0.7);
  dir.position.set(3, 6, 2);
  scene.add(dir);

  const group = new THREE.Group();
  group.rotation.y = DEFAULT_VIEW.yaw;
  group.rotation.x = DEFAULT_VIEW.pitch;
  scene.add(group);

  let raf = 0;
  let disposed = false;
  // Belt-and-braces paint guarantee (FXC-9009): a frame only COUNTS when the
  // canvas had non-trivial size AND the scene had content at render time.
  // setContent resets the flag and starts a short capped retry loop, so a first
  // paint missed for ANY timing reason (layout not settled, hidden tab,
  // offscreen mount) self-heals within ~2s instead of waiting for an unrelated
  // re-render ("3D shows no plot unless I switch trust policy away").
  const MIN_PAINT_PX = 40;
  let contentPainted = false;
  let ensureTimer: ReturnType<typeof setTimeout> | null = null;
  // Read the CURRENT canvas size, resize the renderer/camera to match, and paint.
  // Doing the resize at paint time (not just via the ResizeObserver) is the fix
  // for the blank surface: on mount the bounded canvas may not have laid out yet
  // when the first render fires, so it would paint at the 480×320 fallback and,
  // with preserveDrawingBuffer, that stale/misframed frame persists. Re-syncing
  // the size on every paint guarantees the surface is framed by the real canvas.
  const paintNow = () => {
    if (disposed) return;
    const realW = el.clientWidth;
    const realH = el.clientHeight;
    const w = realW || 480;
    const h = realH || 320;
    renderer.setSize(w, h, true);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);
    if (realW >= MIN_PAINT_PX && realH >= MIN_PAINT_PX && group.children.length > 0) {
      contentPainted = true;
    }
  };
  // Retry paint on a short capped timeout loop until a real content frame lands
  // (~2s). Idempotent and cheap: each tick is at most one render, and the loop
  // stops the moment contentPainted flips (or on dispose / deadline). Timeouts
  // (not rAF) so a backgrounded tab still ticks; visibilitychange also repaints.
  const ensurePainted = () => {
    if (ensureTimer) clearTimeout(ensureTimer);
    const deadline = performance.now() + 2000;
    const tick = () => {
      ensureTimer = null;
      if (disposed || contentPainted) return;
      if (el.clientWidth >= MIN_PAINT_PX && el.clientHeight >= MIN_PAINT_PX) paintNow();
      if (!contentPainted && performance.now() < deadline) {
        ensureTimer = setTimeout(tick, 120);
      }
    };
    ensureTimer = setTimeout(tick, 0);
  };
  const invalidate = () => {
    if (disposed || raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      paintNow();
    });
  };

  const baseLen = Math.hypot(BASE_CAMERA.x, BASE_CAMERA.y, BASE_CAMERA.z);
  const currentView = (): GpView => ({
    yaw: group.rotation.y,
    pitch: group.rotation.x,
    zoom: baseLen / (camera.position.length() || baseLen),
  });
  let commitTimer: ReturnType<typeof setTimeout> | null = null;
  const commitView = () => {
    if (!opts.onViewCommit) return;
    if (commitTimer) clearTimeout(commitTimer);
    commitTimer = setTimeout(() => {
      commitTimer = null;
      opts.onViewCommit?.(currentView());
    }, 200);
  };

  // ── run-sphere raycasting (hover tooltip + click-to-select, FXC-9009) ──────
  const raycaster = new THREE.Raycaster();
  const pointerNdc = new THREE.Vector2();
  let pointMesh: InstanceType<ThreeModule["InstancedMesh"]> | null = null; // set by setContent
  let hoverIndex: number | null = null;
  const hoverAt = (e: PointerEvent): RunHover | null => {
    if (!pointMesh || !opts.onHover) return null;
    const rect = renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    pointerNdc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(pointerNdc, camera);
    const hit = raycaster.intersectObject(pointMesh, false)[0];
    if (hit?.instanceId == null) return null;
    return { index: hit.instanceId, left: e.clientX - rect.left, top: e.clientY - rect.top };
  };
  const updateHover = (hover: RunHover | null) => {
    const next = hover?.index ?? null;
    if (next === hoverIndex && next === null) return; // still off-sphere → no spam
    hoverIndex = next;
    opts.onHover?.(hover);
  };

  let dragging = false; let dragMoved = false; let lx = 0; let ly = 0;
  const onDown = (e: PointerEvent) => { dragging = true; dragMoved = false; lx = e.clientX; ly = e.clientY; };
  const onUp = () => {
    if (dragging && dragMoved) commitView();
    // clean click on a sphere (no drag) → pick it
    if (dragging && !dragMoved && hoverIndex != null) opts.onPick?.(hoverIndex);
    dragging = false;
  };
  const onMove = (e: PointerEvent) => {
    if (!dragging) {
      // idle move over the canvas: raycast for the tooltip
      if (e.target === renderer.domElement) updateHover(hoverAt(e));
      return;
    }
    if (Math.abs(e.clientX - lx) + Math.abs(e.clientY - ly) > 2) dragMoved = true;
    group.rotation.y += (e.clientX - lx) * 0.01;
    group.rotation.x = Math.max(-1.2, Math.min(1.2, group.rotation.x + (e.clientY - ly) * 0.008));
    lx = e.clientX; ly = e.clientY;
    updateHover(null);
    invalidate();
  };
  const onLeave = () => updateHover(null);
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    camera.position.multiplyScalar(e.deltaY > 0 ? 1.08 : 0.93);
    invalidate();
    commitView();
  };
  renderer.domElement.addEventListener("pointerdown", onDown);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointermove", onMove);
  renderer.domElement.addEventListener("pointerleave", onLeave);
  renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

  const ro = new ResizeObserver(() => {
    const w = el.clientWidth || 480; const h = el.clientHeight || 320;
    renderer.setSize(w, h); camera.aspect = w / h; camera.updateProjectionMatrix();
    invalidate();
  });
  ro.observe(el);

  // ── swappable content (surface mesh + projected run points + plate) ─────────
  let disposables: Array<{ dispose(): void }> = [];
  const clearContent = () => {
    for (const d of disposables) d.dispose();
    disposables = [];
    pointMesh = null;
    hoverIndex = null;
    group.clear();
  };

  // Axis line + ticks + labels along one edge of the plate (item 2). All parts
  // live in `group` so they rotate with the surface; sprites billboard.
  const addAxis = (
    axisLabel: string,
    lo: number,
    hi: number,
    place: (t: number) => [number, number, number], // t∈[0,1] → world point on the axis line
    labelOffset: [number, number, number],
  ) => {
    const lineMat = new THREE.LineBasicMaterial({ color: 0x8b93a1, transparent: true, opacity: 0.75 });
    const linePts = [place(0), place(1)].map(([x, y, z]) => new THREE.Vector3(x, y, z));
    const lineGeo = new THREE.BufferGeometry().setFromPoints(linePts);
    group.add(new THREE.Line(lineGeo, lineMat));
    disposables.push(lineGeo, lineMat);
    for (const t of [0, 0.5, 1]) {
      const tick = makeTextSprite(THREE, formatTick(lo + (hi - lo) * t), { height: 0.085 });
      if (!tick) continue;
      const [x, y, z] = place(t);
      tick.sprite.position.set(x + labelOffset[0] * 0.45, y + labelOffset[1] * 0.45, z + labelOffset[2] * 0.45);
      group.add(tick.sprite);
      disposables.push(...tick.disposables);
    }
    const name = makeTextSprite(THREE, axisLabel, { height: 0.115, bold: true, color: "#aab2bf" });
    if (name) {
      const [x, y, z] = place(0.5);
      name.sprite.position.set(x + labelOffset[0], y + labelOffset[1], z + labelOffset[2]);
      group.add(name.sprite);
      disposables.push(...name.disposables);
    }
  };

  const setContent = (slice: SliceResult, runs: ProjectedRun[], meta: ContentMeta) => {
    if (disposed) return;
    clearContent();
    contentPainted = false; // new content — a real frame must land again

    const { xVals, yVals, zMin, zMax } = slice;
    const py = (v: number) => surfaceHeight(v, zMin, zMax);

    // ── the Tailor surface: sliced GP z-grid, coloured by z ──────────────────
    // Built through the SHARED, unit-tested surface-mesh path (gpField.ts) — the
    // same geometry the correlogram cells use, at the same world scale this
    // scene's (2.6,2.2,2.6) fov-42 camera frames.
    const { positions, colors, alphas, indices } = buildSurfaceMeshData(slice);
    if (indices.length) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      // Sigma-fade: RGBA vertex colours (itemSize 4 → three's USE_COLOR_ALPHA).
      // Solid where data-backed, fading to ghost where sigma is high — the
      // trust-surfaces "opacity = confidence" idiom.
      const rgba = new Float32Array((positions.length / 3) * 4);
      for (let vi = 0; vi < positions.length / 3; vi += 1) {
        rgba[vi * 4] = colors[vi * 3]!;
        rgba[vi * 4 + 1] = colors[vi * 3 + 1]!;
        rgba[vi * 4 + 2] = colors[vi * 3 + 2]!;
        rgba[vi * 4 + 3] = alphas[vi] ?? 1;
      }
      geo.setAttribute("color", new THREE.Float32BufferAttribute(rgba, 4));
      geo.setIndex(indices);
      geo.computeVertexNormals();
      const mat = new THREE.MeshStandardMaterial({
        vertexColors: true, side: THREE.DoubleSide,
        transparent: true, roughness: 0.62, metalness: 0.02,
      });
      const mesh = new THREE.Mesh(geo, mat);
      group.add(mesh);
      disposables.push(geo, mat);
    }

    // ── prior ghost (item: "Show prior"): the ROM prior sliced over the SAME
    //    plane/held, built on the MEAN slice's height scale so the two surfaces
    //    are directly comparable. Wireframe + ghost opacity. ───────────────────
    if (meta.priorSlice) {
      const prior = buildSurfaceMeshData(meta.priorSlice, { zMin, zMax });
      if (prior.indices.length) {
        const priorGeo = new THREE.BufferGeometry();
        priorGeo.setAttribute("position", new THREE.Float32BufferAttribute(prior.positions, 3));
        priorGeo.setIndex(prior.indices);
        const priorMat = new THREE.MeshBasicMaterial({
          color: 0x9aa1ad, wireframe: true, transparent: true, opacity: 0.32,
        });
        group.add(new THREE.Mesh(priorGeo, priorMat));
        disposables.push(priorGeo, priorMat);
      }
    }

    // base plate for spatial reference
    const plateGeo = new THREE.PlaneGeometry(LAYOUT.width * 1.04, LAYOUT.depth * 1.04);
    const plateMat = new THREE.MeshBasicMaterial({ color: 0x333842, transparent: true, opacity: 0.08, side: THREE.DoubleSide });
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.rotation.x = -Math.PI / 2;
    plate.position.y = -LAYOUT.height / 2 - 0.02;
    group.add(plate);
    disposables.push(plateGeo, plateMat);

    // ── axes: name + 3 tick values per axis (item 2) ──────────────────────────
    const xLoV = xVals[0] ?? 0;
    const xHiV = xVals[xVals.length - 1] ?? 1;
    const yLoV = yVals[0] ?? 0;
    const yHiV = yVals[yVals.length - 1] ?? 1;
    const hw = LAYOUT.width / 2;
    const hd = LAYOUT.depth / 2;
    const hh = LAYOUT.height / 2;
    // plotX along world X (front edge)
    addAxis(meta.xLabel, xLoV, xHiV, (t) => [-hw + LAYOUT.width * t, -hh - 0.02, hd + 0.14], [0, -0.3, 0.16]);
    // plotY along world Z (right edge)
    addAxis(meta.yLabel, yLoV, yHiV, (t) => [hw + 0.14, -hh - 0.02, -hd + LAYOUT.depth * t], [0.34, -0.3, 0]);
    // output value along world Y (back-left corner)
    addAxis(meta.valueLabel, zMin, zMax, (t) => [-hw - 0.14, -hh + LAYOUT.height * t, -hd - 0.14], [-0.34, 0.16, -0.34]);

    // plane mappers shared by the run spheres and the transport stalks
    const sx = (x: number) => -hw + ((x - xLoV) / ((xHiV - xLoV) || 1)) * LAYOUT.width;
    const sz = (y: number) => -hd + ((y - yLoV) / ((yHiV - yLoV) || 1)) * LAYOUT.depth;

    // ── data-support hull (item: "beyond here is pure model"): the convex hull
    //    of the in-slice runs, traced on the base plate. Outside this outline
    //    NO run sits near the held slice — the surface there is pure model. ────
    if (meta.hull && meta.hull.length >= 3) {
      const hullPts = meta.hull.map((p) => new THREE.Vector3(sx(p.x), -hh - 0.01, sz(p.y)));
      const hullGeo = new THREE.BufferGeometry().setFromPoints(hullPts);
      const hullMat = new THREE.LineBasicMaterial({ color: 0xd9a125, transparent: true, opacity: 0.85 });
      group.add(new THREE.LineLoop(hullGeo, hullMat));
      disposables.push(hullGeo, hullMat);
    }

    // ── transport stalks (corrected mode): a thin vertical line from each run's
    //    RAW height to its CORRECTED height, coloured by |Δ| through the shared
    //    ramp so long transports stand out ("how far did this point move?"). ───
    if (meta.stalks?.length) {
      const maxAbs = Math.max(...meta.stalks.map((s) => Math.abs(s.delta))) || 1;
      const stalkPos: number[] = [];
      const stalkCol: number[] = [];
      for (const stalk of meta.stalks) {
        const wx = sx(stalk.x);
        const wz = sz(stalk.y);
        stalkPos.push(wx, py(stalk.rawValue), wz, wx, py(stalk.correctedValue), wz);
        const [r, g, b] = rampColorRgb(Math.abs(stalk.delta) / maxAbs);
        stalkCol.push(r, g, b, r, g, b);
      }
      const stalkGeo = new THREE.BufferGeometry();
      stalkGeo.setAttribute("position", new THREE.Float32BufferAttribute(stalkPos, 3));
      stalkGeo.setAttribute("color", new THREE.Float32BufferAttribute(stalkCol, 3));
      const stalkMat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.7 });
      group.add(new THREE.LineSegments(stalkGeo, stalkMat));
      disposables.push(stalkGeo, stalkMat);
    }

    // ── projected runs: each run at (axisX, axisY) with its MEASURED value as
    //    the height, so you see data-vs-fit. Mapped through the slice's own
    //    node range (the surface spans [xVals[0], xVals[last]] etc.).
    //    Optionally coloured per-run (colour-by axis, item 3a). ────────────────
    if (runs.length) {
      const hasColors = !!meta.pointColors && meta.pointColors.length === runs.length;
      const sphereGeo = new THREE.SphereGeometry(0.026, 10, 8);
      // white base when per-instance colours multiply in; the classic light grey otherwise
      const sphereMat = new THREE.MeshStandardMaterial({ color: hasColors ? 0xffffff : 0xe2e8f0, roughness: 0.5, metalness: 0.05 });
      const mesh = new THREE.InstancedMesh(sphereGeo, sphereMat, runs.length);
      const dummy = new THREE.Object3D();
      const instColor = new THREE.Color();
      for (let k = 0; k < runs.length; k += 1) {
        const run = runs[k]!;
        dummy.position.set(sx(run.x), py(run.value), sz(run.y));
        dummy.updateMatrix();
        mesh.setMatrixAt(k, dummy.matrix);
        if (hasColors) {
          const [r, g, b] = meta.pointColors![k]!;
          mesh.setColorAt(k, instColor.setRGB(r, g, b));
        }
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      group.add(mesh);
      disposables.push(sphereGeo, sphereMat, mesh);
      pointMesh = mesh; // raycast target for hover/pick
    }

    paint();
    ensurePainted(); // belt-and-braces: retry until a real content frame lands
  };

  // Paint now (so a frame lands immediately) AND again next frame (so a bounded
  // canvas whose layout / ResizeObserver settles a tick later is re-framed).
  let paintRaf = 0;
  const paint = () => {
    if (disposed) return;
    paintNow();
    if (paintRaf) cancelAnimationFrame(paintRaf);
    paintRaf = requestAnimationFrame(() => { paintRaf = 0; paintNow(); });
  };

  // Classic missed-paint causes: a hidden tab (rAF suspended) or an offscreen
  // mount at content time. Repaint when the document becomes visible and when
  // the canvas actually intersects the viewport — both idempotent.
  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      paint();
      if (!contentPainted) ensurePainted();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  const io = typeof IntersectionObserver !== "undefined"
    ? new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          paint();
          if (!contentPainted) ensurePainted();
        }
      })
    : null;
  io?.observe(el);

  const applyView = (view: GpView) => {
    if (disposed) return;
    group.rotation.y = view.yaw;
    group.rotation.x = Math.max(-1.2, Math.min(1.2, view.pitch));
    const zoom = Number.isFinite(view.zoom) && view.zoom > 0.05 ? view.zoom : 1;
    camera.position.setLength(baseLen / zoom);
    invalidate();
  };

  const dispose = () => {
    disposed = true;
    if (raf) cancelAnimationFrame(raf);
    if (paintRaf) cancelAnimationFrame(paintRaf);
    if (ensureTimer) clearTimeout(ensureTimer);
    if (commitTimer) clearTimeout(commitTimer);
    ro.disconnect();
    io?.disconnect();
    document.removeEventListener("visibilitychange", onVisibility);
    renderer.domElement.removeEventListener("pointerdown", onDown);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointermove", onMove);
    renderer.domElement.removeEventListener("pointerleave", onLeave);
    renderer.domElement.removeEventListener("wheel", onWheel);
    clearContent();
    renderer.dispose();
    if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
  };

  return { setContent, applyView, paint, dispose };
}

// ── held-value slider ────────────────────────────────────────────────────────
function HeldSlider({
  axis, label, values, value, onChange,
}: {
  axis: string; label: string; values: number[]; value: number; onChange: (v: number) => void;
}) {
  const lo = values[0] ?? 0;
  const hi = values[values.length - 1] ?? 1;
  // step: the smallest gap between adjacent nodes, so the thumb can land on each
  // node (non-uniform grids like flap 0/10/30/40 still reach every node).
  const step = useMemo(() => {
    let min = Infinity;
    for (let i = 1; i < values.length; i += 1) min = Math.min(min, Math.abs(values[i]! - values[i - 1]!));
    return Number.isFinite(min) && min > 0 ? min : (hi - lo) / 40 || 1;
  }, [values, hi, lo]);
  return (
    <label className="v5-adb-gp-held" data-testid={`gp-held-${axis}`} title={`${label}: ${lo} → ${hi}`}>
      <span className="v5-adb-gp-held-label">{label}</span>
      <input
        type="range"
        min={lo}
        max={hi}
        step={step}
        value={value}
        data-testid={`gp-held-slider-${axis}`}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="v5-adb-gp-held-val">{value}</span>
    </label>
  );
}

export function TailorGpSurface({
  projectId, branch, quantity, axisX, axisY, fill, rows, axisLabels, onUnavailable, onPlaneChange, onRunSelect,
}: Props) {
  // kept in a ref: the imperative scene controller's pick handler reads it.
  const onRunSelectRef = useRef(onRunSelect);
  onRunSelectRef.current = onRunSelect;
  const queryEnabled = !!projectId && !!quantity;
  const query = useQuery({
    queryKey: ["composite-gp-field", projectId, branch, quantity],
    queryFn: ({ signal }) => fetchGpField({ projectId, branch, quantity, signal }),
    enabled: queryEnabled,
  });
  const field = query.data;
  const available = field?.available === true;
  const gpField = available ? (field as GpFieldAvailable) : null;

  // Tell the parent whether to fall back to /surface, WITH the reason:
  //   "no-field" — a real 200 said no baked field exists (legit, sticky);
  //   "error"    — the fetch failed (transient: the parent should re-probe,
  //                never lock the fallback forever).
  // A pending/disabled query signals NOTHING — the parent keeps this panel
  // mounted and this panel renders an explicit visible state (never null).
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;
  useEffect(() => {
    if (query.isSuccess) {
      const noField = field?.available === false;
      if (noField) onUnavailableRef.current?.(true, "no-field");
      else onUnavailableRef.current?.(false);
    } else if (query.isError) {
      onUnavailableRef.current?.(true, "error");
    }
  }, [query.isSuccess, query.isError, field]);

  // ── held-value state: seeded from the field's midpoints, one entry per axis.
  //    Dragging a slider mutates this → the slice recomputes locally. ──────────
  const [held, setHeld] = useState<Record<string, number>>({});
  useEffect(() => {
    if (gpField) setHeld(defaultHeld(gpField));
  }, [gpField?.fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  const domains = useMemo(() => (gpField ? fieldAxisDomains(gpField) : []), [gpField]);
  const varyingDomains = useMemo(() => domains.filter((d) => d.varying), [domains]);
  const fixedDomains = useMemo(() => domains.filter((d) => !d.varying), [domains]);

  // Local X/Y axis override from the pickers (null = follow the caller's axes).
  const [override, setOverride] = useState<{ x: string | null; y: string | null }>({ x: null, y: null });
  useEffect(() => { setOverride({ x: null, y: null }); }, [gpField?.fingerprint]);

  // The two plotted axes must be varying + distinct; if the caller's axisX/axisY
  // (or the override) aren't slideable in this field, fall back to the first two
  // varying axes.
  const { plotX, plotY } = useMemo(() => {
    const varyingIds = varyingDomains.map((d) => d.axis);
    if (!varyingIds.length) return { plotX: axisX, plotY: axisY };
    const wantX = override.x ?? axisX;
    const wantY = override.y ?? axisY;
    const x = varyingIds.includes(wantX) ? wantX : varyingIds[0]!;
    let y = varyingIds.includes(wantY) && wantY !== x ? wantY : varyingIds.find((id) => id !== x) ?? x;
    if (y === x) y = varyingIds.find((id) => id !== x) ?? y;
    return { plotX: x, plotY: y };
  }, [axisX, axisY, override, varyingDomains]);

  const pickX = (x: string) => setOverride((cur) => ({ x, y: cur.y === x ? null : cur.y }));
  const pickY = (y: string) => setOverride((cur) => ({ x: cur.x === y ? null : cur.x, y }));

  // Report the ACTUALLY-plotted plane up (rail trust map follows the 3D view).
  const onPlaneChangeRef = useRef(onPlaneChange);
  onPlaneChangeRef.current = onPlaneChange;
  useEffect(() => {
    if (gpField && plotX && plotY && plotX !== plotY) onPlaneChangeRef.current?.({ x: plotX, y: plotY });
  }, [gpField, plotX, plotY]);

  // ── the client-side slice — recomputed on any held/axis change (real time) ──
  const slice = useMemo(() => {
    if (!gpField || plotX === plotY) return null;
    // held for the OFF-PLANE axes only (plotX/plotY are swept, not held)
    const offPlaneHeld: Record<string, number> = {};
    for (const [axis, v] of Object.entries(held)) {
      if (axis !== plotX && axis !== plotY) offPlaneHeld[axis] = v;
    }
    return sliceGpField(gpField, { axisX: plotX, axisY: plotY, nx: SLICE_RES, ny: SLICE_RES, held: offPlaneHeld });
  }, [gpField, plotX, plotY, held]);

  // ── data-era filter: gap-clustered created_at sessions as toggle chips, so
  //    legacy structured sweeps separate from Tailor-picked data. HEURISTIC
  //    clustering (gap > 6h starts a new era) — labelled as such in the UI.
  //    Unstamped rows are never hidden. The SURFACE stays the full fit. ────────
  const eraData = useMemo(() => clusterEras(rows ?? []), [rows]);
  const [hiddenEras, setHiddenEras] = useState<Set<string>>(() => new Set());
  useEffect(() => { setHiddenEras(new Set()); }, [eraData]);
  const eraRows = useMemo(
    () => (rows ? filterRowsByEras(rows, eraData?.eraByRow ?? null, hiddenEras) : rows),
    [rows, eraData, hiddenEras],
  );
  const eraFiltered = hiddenEras.size > 0 && !!rows && !!eraRows && eraRows.length !== rows.length;
  const toggleEra = useCallback((eraId: string) => {
    setHiddenEras((cur) => {
      const next = new Set(cur);
      if (next.has(eraId)) next.delete(eraId);
      else next.add(eraId);
      return next;
    });
  }, []);

  // ── budget replay (item 5b): show only the first-N runs chronologically,
  //    WITHIN the era-visible set (the two filters compose: era first, then
  //    budget). The SURFACE stays the full fit — only the points replay. ──────
  const rankMap = useMemo(() => chronoRankMap(eraRows ?? []), [eraRows]);
  const rankTotal = rankMap?.size ?? 0;
  const [budget, setBudget] = useState<number | null>(null); // null = all
  useEffect(() => { setBudget(null); }, [gpField?.fingerprint, rankTotal]); // eslint-disable-line react-hooks/exhaustive-deps
  const effectiveBudget = Math.min(budget ?? rankTotal, rankTotal);
  const budgetActive = rankMap !== null && effectiveBudget < rankTotal;
  const budgetRows = useMemo(
    () => (eraRows ? filterRowsByBudget(eraRows, rankMap, effectiveBudget) : eraRows),
    [eraRows, rankMap, effectiveBudget],
  );

  const runs = useMemo(() => projectRuns(budgetRows, plotX, plotY, quantity), [budgetRows, plotX, plotY, quantity]);

  // ── prior toggle: the ROM prior sliced over the SAME plane/held so the
  //    flexfoil prior is visibly compared against the corrected surface. ──────
  const [showPrior, setShowPrior] = useState(false);
  useEffect(() => { setShowPrior(false); }, [gpField?.fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps
  const priorSlice = useMemo(() => {
    if (!showPrior || !gpField?.prior || plotX === plotY) return null;
    const offPlaneHeld: Record<string, number> = {};
    for (const [axis, v] of Object.entries(held)) {
      if (axis !== plotX && axis !== plotY) offPlaneHeld[axis] = v;
    }
    return sliceGpField(gpField, { axisX: plotX, axisY: plotY, nx: SLICE_RES, ny: SLICE_RES, held: offPlaneHeld, source: "prior" });
  }, [showPrior, gpField, plotX, plotY, held]);

  // ── data-support hull: convex hull of the runs NEAR the held slice — the
  //    hard boundary beyond which the plotted plane is pure model. ────────────
  const hull = useMemo(() => {
    if (!gpField || !runs.length) return null;
    const inSlice = applyEvidenceMode(gpField, runs, { axisX: plotX, axisY: plotY, held, mode: "compatible" });
    const points = convexHull2d(inSlice.map((run) => ({ x: run.x, y: run.y })));
    return points.length ? points : null;
  }, [gpField, runs, plotX, plotY, held]);

  // ── point-evidence mode (item 4): Raw / Compatible / Corrected, powered by
  //    the GP itself. The SURFACE never changes — only the projected points. ──
  const [pointMode, setPointMode] = useState<GpEvidenceMode>("raw");
  const displayRuns = useMemo(() => {
    if (!gpField || pointMode === "raw") return runs;
    return applyEvidenceMode(gpField, runs, { axisX: plotX, axisY: plotY, held, mode: pointMode });
  }, [gpField, runs, plotX, plotY, held, pointMode]);

  // ── transport distance (item: "how can these points be 'corrected'?") ──────
  //    In corrected mode displayRuns aligns 1:1 with runs, so deltas/stalks are
  //    exact per-run transports. Drives stalks, colour-by-|Δ|, tooltip, caption.
  const transportStalksData = useMemo(
    () => (pointMode === "corrected" && gpField ? buildTransportStalks(runs, displayRuns) : null),
    [pointMode, gpField, runs, displayRuns],
  );
  const transportInfo = useMemo(
    () => (transportStalksData ? transportStats(transportStalksData) : null),
    [transportStalksData],
  );
  const deltas = useMemo(
    () => (pointMode === "corrected" ? transportDeltas(runs, displayRuns) : null),
    [pointMode, runs, displayRuns],
  );

  // Excluded remainder (budget replay, corrected mode — cheap): how far would
  // the yet-unsolved runs transport? Large mean|Δ| = the shown budget misses them.
  const remainderInfo = useMemo(() => {
    if (!budgetActive || pointMode !== "corrected" || !gpField || !eraRows || !rankMap) return null;
    // remainder within the era-VISIBLE set (the two filters compose)
    const excluded = eraRows.filter((row) => {
      const rank = rankMap.get(row);
      return rank !== undefined && rank >= effectiveBudget;
    });
    const excludedRuns = projectRuns(excluded, plotX, plotY, quantity);
    if (!excludedRuns.length) return null;
    const corrected = applyEvidenceMode(gpField, excludedRuns, { axisX: plotX, axisY: plotY, held, mode: "corrected" });
    const absDeltas = transportDeltas(excludedRuns, corrected).map(Math.abs);
    return { count: excludedRuns.length, meanAbs: absDeltas.reduce((a, b) => a + b, 0) / absDeltas.length };
  }, [budgetActive, pointMode, gpField, eraRows, rankMap, effectiveBudget, plotX, plotY, quantity, held]);

  // ── GP training points ("which 500", FXC-9009): the rows that ACTUALLY
  //    trained the residual GP, from the baked payload (post re-bake). ────────
  const trainedKeys = useMemo(
    () => new Set(gpField?.trained_condition_keys ?? []),
    [gpField],
  );

  // ── colour-by (item 3a): colour the run spheres by any field axis via the
  //    shared ramp; "" = uniform (classic light grey). Normalised over the
  //    AXIS DOMAIN (stable under filtering/transport). "transport |Δ|" is an
  //    extra option in corrected mode; "solve order" when rows are timestamped;
  //    "GP training point" when the baked field ships its trained keys. ───────
  const [colorBy, setColorBy] = useState<string>("");
  useEffect(() => { setColorBy(""); }, [gpField?.fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps
  // leaving corrected mode invalidates the transport colour choice
  useEffect(() => {
    if (pointMode !== "corrected" && colorBy === TRANSPORT_COLOR_KEY) setColorBy("");
  }, [pointMode, colorBy]);
  const colorDomain = useMemo(() => {
    if (!colorBy) return null;
    if (colorBy === TRANSPORT_COLOR_KEY) {
      return { min: 0, max: transportInfo?.max || 1 };
    }
    if (colorBy === ORDER_COLOR_KEY) {
      return rankTotal >= 2 ? { min: 1, max: rankTotal } : null;
    }
    if (colorBy === TRAINING_COLOR_KEY) {
      // binary highlight — a nominal domain so pointColors runs; no ramp bar
      return trainedKeys.size ? { min: 0, max: 1 } : null;
    }
    const d = domains.find((item) => item.axis === colorBy);
    return d && d.max > d.min ? { min: d.min, max: d.max } : null;
  }, [colorBy, domains, transportInfo, rankTotal, trainedKeys]);
  const pointColors = useMemo<Array<[number, number, number]> | null>(() => {
    if (!colorDomain) return null;
    const span = colorDomain.max - colorDomain.min || 1;
    if (colorBy === TRANSPORT_COLOR_KEY) {
      if (!deltas) return null;
      return displayRuns.map((_, index) => rampColorRgb(Math.abs(deltas[index] ?? 0) / span));
    }
    if (colorBy === ORDER_COLOR_KEY) {
      if (!rankMap) return null;
      return displayRuns.map((run) => {
        const rank = run.row ? rankMap.get(run.row) : undefined;
        if (rank === undefined) return [0.62, 0.65, 0.7]; // unstamped → neutral grey
        return rampColorRgb(rank / Math.max(1, rankTotal - 1));
      });
    }
    if (colorBy === TRAINING_COLOR_KEY) {
      return trainingPointColors(displayRuns, trainedKeys);
    }
    return displayRuns.map((run) => {
      const raw = run.row?.[colorBy];
      const v = typeof raw === "number" && Number.isFinite(raw) ? raw : Number(raw);
      if (!Number.isFinite(v)) return [0.62, 0.65, 0.7]; // no value → neutral grey
      return rampColorRgb((v - colorDomain.min) / span);
    });
  }, [displayRuns, colorBy, colorDomain, deltas, rankMap, rankTotal, trainedKeys]);

  // ── hover tooltip (item 3b): raycast hit index + canvas-local position ──────
  const [hover, setHover] = useState<RunHover | null>(null);
  useEffect(() => { setHover(null); }, [displayRuns]);
  const hoverRun = hover ? displayRuns[hover.index] ?? null : null;

  const label = (axis: string) => axisLabels?.[axis] ?? axis;

  // ── persistent scene controller ─────────────────────────────────────────────
  const mountRef = useRef<HTMLDivElement | null>(null);
  const ctrlRef = useRef<SceneCtrl | null>(null);
  const ctrlPromiseRef = useRef<Promise<SceneCtrl | null> | null>(null);
  // Latest slice/runs/meta in refs so the async ctrl-resolve path (and any
  // deferred paint) always reads CURRENT content — not the stale closure it was
  // created in. This is the boot-safe discipline: when three.js finishes
  // importing, the fingerprint reseed / axis reset / held churn may already have
  // produced newer slices; building from refs guarantees the surface reflects
  // them (the fix for the blank 3D tab after the 13-axis re-bake).
  const sliceRef = useRef(slice);
  sliceRef.current = slice;
  const runsRef = useRef(displayRuns);
  runsRef.current = displayRuns;
  const metaRef = useRef<ContentMeta>({ xLabel: plotX, yLabel: plotY, valueLabel: quantity, pointColors });
  metaRef.current = {
    xLabel: label(plotX),
    yLabel: label(plotY),
    valueLabel: quantity,
    pointColors,
    stalks: transportStalksData,
    hull,
    priorSlice,
  };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let cancelled = false;
    const ready = (ctrl: SceneCtrl | null) => {
      if (!ctrl || cancelled) return;
      const s = sliceRef.current;
      if (!s) return;
      ctrl.setContent(s, runsRef.current, metaRef.current); // setContent already paints (now + next frame)
    };
    if (ctrlRef.current) {
      ready(ctrlRef.current);
      return;
    }
    if (!ctrlPromiseRef.current) {
      ctrlPromiseRef.current = createSceneCtrl(mount, {
        onHover: (h) => setHover(h),
        onPick: (index) => {
          const row = runsRef.current[index]?.row;
          if (row) onRunSelectRef.current?.(row);
        },
      })
        .then((ctrl) => { ctrlRef.current = ctrl; return ctrl; })
        .catch((err: unknown) => {
          // WebGL/headless failures must not crash the panel — log + bail.
          // eslint-disable-next-line no-console
          console.warn("TailorGpSurface: 3D render unavailable", err);
          ctrlPromiseRef.current = null;
          return null;
        });
    }
    void ctrlPromiseRef.current.then(ready);
    return () => { cancelled = true; };
  }, [slice, displayRuns, pointColors, transportStalksData, hull, priorSlice]);

  useEffect(() => () => {
    ctrlRef.current?.dispose();
    ctrlRef.current = null;
    ctrlPromiseRef.current = null;
  }, []);

  // ── render — EXHAUSTIVE states, never a silent null (FXC-9009 live bug):
  // react-query v5's isLoading is isPending && isFetching, so a pending-but-
  // NOT-fetching query (disabled, or paused by networkMode/onlineManager) has
  // isLoading === false with no data and no error. The old chain fell through
  // to `return null` WITHOUT signalling onUnavailable — the parent then kept
  // this (blank) panel mounted forever: header, no surface, no fallback, no
  // errors. Every state now renders something visible. ────────────────────────
  if (!queryEnabled) {
    return <div className="v5-adbc-chart-empty" data-testid="gp-surface-blocked"><Icons.Refresh size={16} />Waiting for an output selection…</div>;
  }
  if (query.isPending) {
    // v5: pending covers loading AND paused/not-yet-fetching — no silent gap.
    return <div className="v5-adbc-chart-empty" data-testid="gp-surface-loading"><Icons.Refresh size={16} />Loading Tailor field…</div>;
  }
  if (query.isError) {
    // onUnavailable(true, "error") already fired — the parent swaps to the
    // fitted-surface fallback and re-probes. This note only bridges the swap.
    return <div className="v5-adbc-chart-empty" data-testid="gp-surface-error"><Icons.Info size={16} />Tailor field fetch failed — using fitted surfaces, will retry…</div>;
  }
  if (!available || !gpField) {
    // A real 200 said no baked field — onUnavailable(true, "no-field") fired;
    // the parent swaps to the fitted-surface fallback. Visible bridge only.
    return <div className="v5-adbc-chart-empty" data-testid="gp-surface-nofield"><Icons.Info size={16} />No baked Tailor field for {quantity} — using fitted surfaces…</div>;
  }
  if (varyingDomains.length < 2) {
    return <div className="v5-adbc-chart-empty" data-testid="gp-surface-degenerate">Tailor field varies on fewer than two axes — nothing to slice in 3D.</div>;
  }

  return (
    <div data-testid="tailor-gp-surface" className={fill ? "tl-fill" : undefined}>
      <div className="v5-adb-tailor-strip" style={{ marginBottom: 8, alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span className="v5-adbc-panel-note" data-testid="gp-surface-note">
          <Icons.Sliders size={12} /> Tailor residual-GP · sliced live
          {gpField.prior_share != null ? ` · prior ${Math.round(gpField.prior_share * 100)}%` : ""}
          {/* honest training cap: never flatter bulk — the GP saw n_residual of
              hf_solves distinct HF solves (the pipeline's fit-point subsample) */}
          {gpField.n_residual != null
            ? gpField.hf_solves != null && gpField.hf_solves > gpField.n_residual
              ? ` · GP trained on ${gpField.n_residual.toLocaleString()} of ${gpField.hf_solves.toLocaleString()} solves (pipeline cap)`
              : ` · ${gpField.n_residual} residual pts`
            : ""}
          {gpField.stale ? " · ⚠ stale" : ""}
        </span>
        {/* axisX / axisY pickers — only the slideable (varying) axes */}
        <label className="v5-adb-compact-select" data-testid="gp-axis-x">
          <span>X</span>
          <select value={plotX} onChange={(e) => pickX(e.target.value)} data-testid="gp-axis-x-select">
            {varyingDomains.map((d) => <option key={d.axis} value={d.axis}>{label(d.axis)}</option>)}
          </select>
        </label>
        <label className="v5-adb-compact-select" data-testid="gp-axis-y">
          <span>Y</span>
          <select value={plotY} onChange={(e) => pickY(e.target.value)} data-testid="gp-axis-y-select">
            {varyingDomains.map((d) => <option key={d.axis} value={d.axis}>{label(d.axis)}</option>)}
          </select>
        </label>
        {/* point-evidence mode (item 4): affects the run POINTS only — the
            surface is the GP and never blanks. Corrected = Tailor transport. */}
        <div className="v5-adb-evidence-tabs" role="group" aria-label="Point evidence mode" data-testid="gp-point-mode">
          {([
            { id: "raw", title: "Every run at its measured value." },
            { id: "compatible", title: "Only runs near the held slice (off-plane axes within tolerance)." },
            { id: "corrected", title: "Runs transported onto the held slice: raw + GP(held) − GP(own)." },
          ] as Array<{ id: GpEvidenceMode; title: string }>).map((m) => (
            <button key={m.id} type="button" title={m.title}
                    className={pointMode === m.id ? "is-active" : ""}
                    data-testid={`gp-point-mode-${m.id}`}
                    onClick={() => setPointMode(m.id)}>
              {m.id === "raw" ? "Raw" : m.id === "compatible" ? "Compatible" : "Corrected"}
            </button>
          ))}
        </div>
        {/* colour-by (item 3a): run spheres coloured by any field axis; in
            corrected mode also by transport |Δ| so big transports glow */}
        <label className="v5-adb-compact-select" data-testid="gp-color-by">
          <span>Colour points by</span>
          <select value={colorBy} onChange={(e) => setColorBy(e.target.value)} data-testid="gp-color-by-select">
            <option value="">uniform</option>
            {pointMode === "corrected" ? (
              <option value={TRANSPORT_COLOR_KEY} data-testid="gp-color-by-transport">transport |Δ|</option>
            ) : null}
            {rankMap ? (
              <option value={ORDER_COLOR_KEY} data-testid="gp-color-by-order">solve order</option>
            ) : null}
            {trainedKeys.size ? (
              <option value={TRAINING_COLOR_KEY} data-testid="gp-color-by-training">GP training point</option>
            ) : null}
            {domains.map((d) => <option key={d.axis} value={d.axis}>{label(d.axis)}</option>)}
          </select>
        </label>
        {colorBy === TRAINING_COLOR_KEY && colorDomain ? (
          // binary legend, not a ramp: which runs the GP actually saw
          <span className="v5-adb-gp-colorbar" data-testid="gp-training-legend" title={`${trainedKeys.size.toLocaleString()} runs trained the GP (pipeline cap); everything else is a spectator the fit never saw.`}>
            <i style={{ background: "rgb(240, 158, 41)", width: 10 }} />
            <em>in GP fit ({trainedKeys.size.toLocaleString()})</em>
            <i style={{ background: "rgb(107, 115, 128)", width: 10 }} />
            <em>spectator</em>
          </span>
        ) : colorBy && colorDomain ? (
          <span className="v5-adb-gp-colorbar" data-testid="gp-colorbar" title={`${colorBy === TRANSPORT_COLOR_KEY ? "transport |Δ|" : label(colorBy)}: ${formatTick(colorDomain.min)} → ${formatTick(colorDomain.max)}`}>
            <em>{formatTick(colorDomain.min)}</em>
            <i style={{ background: `linear-gradient(90deg, ${[0, 0.25, 0.5, 0.75, 1].map((t) => rampColorCss(t)).join(", ")})` }} />
            <em>{formatTick(colorDomain.max)}</em>
          </span>
        ) : null}
        {/* data eras: HEURISTIC gap-clustered created_at sessions as toggle
            chips — hide legacy sweeps, keep Tailor-picked data (or vice versa) */}
        {eraData ? (
          <span
            className="v5-adb-gp-fixed"
            style={{ marginBottom: 0 }}
            data-testid="gp-eras"
            title="Data eras — heuristic gap-based session clustering of run created_at timestamps (gap > 6h starts a new era). Toggle a chip to hide/show that era's points; the surface stays the full fit."
          >
            {eraData.eras.map((era) => (
              <button
                key={era.id}
                type="button"
                className={`v5-adb-chip${hiddenEras.has(era.id) ? "" : " is-active"}`}
                data-testid={`gp-era-${era.id}`}
                aria-pressed={!hiddenEras.has(era.id)}
                onClick={() => toggleEra(era.id)}
              >
                {era.label} ({era.count.toLocaleString()})
              </button>
            ))}
          </span>
        ) : null}
        {/* budget replay (item 5b): first-N runs chronologically; surface = full fit */}
        {rankMap ? (
          <label className="v5-adb-gp-held" data-testid="gp-budget" title="Budget replay: show only the first N runs chronologically — watch where Tailor spent its later picks. The surface stays the full fit.">
            <span className="v5-adb-gp-held-label">Budget replay</span>
            <input
              type="range"
              min={1}
              max={rankTotal}
              step={1}
              value={effectiveBudget}
              data-testid="gp-budget-slider"
              onChange={(e) => setBudget(Number(e.target.value))}
            />
            <span className="v5-adb-gp-held-val">{effectiveBudget}/{rankTotal}</span>
          </label>
        ) : null}
        {/* prior toggle: ghost wireframe of the ROM prior for comparison */}
        {gpField.prior ? (
          <label className="v5-adb-gp-held" data-testid="gp-show-prior" title="Overlay the ROM prior surface (ghost wireframe) sliced over the same plane — compare the prior against the corrected Tailor surface.">
            <input
              type="checkbox"
              checked={showPrior}
              data-testid="gp-show-prior-checkbox"
              onChange={(e) => setShowPrior(e.target.checked)}
            />
            <span className="v5-adb-gp-held-label">
              Show prior{gpField.prior_share != null ? ` — flexfoil prior (${Math.round(gpField.prior_share * 100)}%)` : ""}
            </span>
          </label>
        ) : null}
      </div>

      {/* held-value sliders for the OFF-PLANE varying axes */}
      <div className="v5-adb-gp-helds" data-testid="gp-held-sliders">
        {varyingDomains
          .filter((d) => d.axis !== plotX && d.axis !== plotY)
          .map((d) => (
            <HeldSlider
              key={d.axis}
              axis={d.axis}
              label={label(d.axis)}
              values={d.values}
              value={Number.isFinite(held[d.axis]) ? held[d.axis]! : axisMidpoint(d.values)}
              onChange={(v) => setHeld((cur) => ({ ...cur, [d.axis]: v }))}
            />
          ))}
      </div>

      {/* geometry axes — fixed, single design; cannot slide without re-baking */}
      {fixedDomains.length ? (
        <div className="v5-adb-gp-fixed" data-testid="gp-fixed-chips">
          {fixedDomains.map((d) => (
            <span
              key={d.axis}
              className="v5-adb-chip is-fixed"
              data-testid={`gp-fixed-${d.axis}`}
              aria-disabled="true"
              title={`${label(d.axis)} is fixed at ${d.values[0]} — a single geometry design. Slicing over it would need the field re-baked over that axis.`}
            >
              <Icons.Lock size={11} /> {label(d.axis)} = {d.values[0]} · fixed — single design
            </span>
          ))}
        </div>
      ) : null}

      <div style={{ position: "relative" }}>
        <div
          ref={mountRef}
          className="v5-adbc-surface-canvas"
          data-testid="gp-surface-canvas"
          // Explicit bounded height (NOT auto/flex) so the ResizeObserver reads a
          // fixed clientHeight — height:auto here fed a RO→setSize→grow loop that
          // blew the canvas to ~16M px tall (invisible surface).
          style={{
            height: fill ? "min(62vh, 640px)" : 420,
            minHeight: 360,
            position: "relative",
            overflow: "hidden",
          }}
        />
        {/* hover provenance (item 3b): where does this run come from? */}
        {hover && hoverRun ? (
          <div
            className="v5-adb-gp-tooltip"
            data-testid="gp-run-tooltip"
            style={{ left: Math.max(8, hover.left + 14), top: Math.max(8, hover.top + 12) }}
          >
            <strong>{String(hoverRun.row?._row_id ?? hoverRun.row?._node_id ?? "run")}</strong>
            {(() => {
              const key = runConditionKey(hoverRun);
              return key && trainedKeys.has(key) ? (
                <span data-testid="gp-tooltip-training">in GP fit — one of the {trainedKeys.size.toLocaleString()} training points</span>
              ) : null;
            })()}
            {pointMode === "corrected" && deltas && Math.abs(deltas[hover.index] ?? 0) > 1e-12 ? (
              <span data-testid="gp-tooltip-transport">
                {quantity}: raw {formatTick(runs[hover.index]?.value ?? Number.NaN)} → corrected {formatTick(hoverRun.value)} · Δ {formatTick(deltas[hover.index]!)}
              </span>
            ) : (
              <span>
                {quantity} = {formatTick(hoverRun.value)}
                {pointMode === "corrected" ? " · on the held slice" : ""}
              </span>
            )}
            {gpField.axes.map((axis) => {
              const raw = hoverRun.row?.[axis];
              const v = typeof raw === "number" && Number.isFinite(raw) ? raw : Number(raw);
              if (!Number.isFinite(v)) return null;
              return <span key={axis}>{label(axis)} = {formatTick(v)}</span>;
            })}
            {typeof hoverRun.row?._node_id === "string" && hoverRun.row._node_id ? (
              <em>node {String(hoverRun.row._node_id)} · click to inspect</em>
            ) : (
              <em>click to inspect</em>
            )}
          </div>
        ) : null}
      </div>
      <div className="v5-adbc-panel-note" data-testid="gp-surface-caption">
        {plotX} × {plotY} · surface = Tailor GP{budgetActive ? " (full fit)" : ""} · faded = unsupported by data (high sigma)
        {hull ? " · gold outline = data-support hull (beyond = pure model)" : ""}
        {showPrior && priorSlice ? " · wireframe = prior" : ""}
        {eraFiltered && rows && eraRows ? ` · points filtered: ${eraRows.length} of ${rows.length} rows shown (era filter)` : ""}
        {budgetActive ? ` · points = first ${effectiveBudget} of ${rankTotal} (budget replay)` : ""}
        {remainderInfo ? ` · remainder mean|Δ| ${formatTick(remainderInfo.meanAbs)} over ${remainderInfo.count}` : ""}
        {" "}· spheres = {displayRuns.length}/{runs.length} run{runs.length === 1 ? "" : "s"} ({pointMode})
        {transportInfo ? ` · transport: mean ${formatTick(transportInfo.mean)} · max ${formatTick(transportInfo.max)}` : ""}
        {" "}· hover a sphere for provenance · drag to rotate · wheel to zoom
      </div>
    </div>
  );
}
